import { useEffect, useRef, useState } from 'react'
import useMediaQuery from 'hooks/use-media-query'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { CREDENCIALES_INTRO_ID, CREDENCIALES_VIDEOS, credencialesImage, credencialesVideo } from '../constants'

const track = (element: string) =>
  dlTrackEvent('user_interaction', { action: 'click', element, section: 'credenciales' })

// Full-bleed looping video hero (Figma desktop 5631:1569 / mobile 5854:1293)
// with a sound toggle and a scroll arrow. The video element is local (not
// VideoBackground) because that component pins `muted` to true.
const Hero = () => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const [muted, setMuted] = useState(true)

  const src = credencialesVideo(isDesktop ? CREDENCIALES_VIDEOS.heroDesktop : CREDENCIALES_VIDEOS.heroMobile)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    // React 17 does not render the muted attribute: set it by hand so the
    // autoplay policy lets the loop start.
    video.muted = muted
    if (muted) video.setAttribute('muted', '')
    else video.removeAttribute('muted')
    video.play().catch(() => undefined)
  }, [src, muted])

  const toggleSound = () => {
    const next = !muted
    track(`hero :: sonido ${next ? 'off' : 'on'}`)
    setMuted(next)
  }

  const scrollToIntro = () => {
    track('hero :: scroll')
    document.getElementById(CREDENCIALES_INTRO_ID)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="credenciales__hero">
      <video
        ref={videoRef}
        key={src}
        className="credenciales__hero-video"
        src={src}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
      <div className="credenciales__hero-fade" aria-hidden="true" />
      <button
        type="button"
        className="credenciales__hero-sound"
        aria-pressed={!muted}
        aria-label={muted ? 'Activar sonido' : 'Silenciar'}
        onClick={toggleSound}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5H4Z" fill="currentColor" />
          {muted ? (
            <path d="m16 9 5 6m0-6-5 6" strokeLinecap="round" />
          ) : (
            <>
              <path d="M15.5 9.2a4 4 0 0 1 0 5.6" strokeLinecap="round" />
              <path d="M18 6.5a8 8 0 0 1 0 11" strokeLinecap="round" />
            </>
          )}
        </svg>
      </button>
      <button
        type="button"
        className="credenciales__hero-arrow"
        aria-label="Bajar a la introducción"
        style={{ backgroundImage: `url(${credencialesImage('DESKTOP', 'button_full_desk_2x')})` }}
        onClick={scrollToIntro}
      />
    </section>
  )
}

export default Hero
